import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner, Row, Col } from 'react-bootstrap';
import DatosMaestrosService from '../../services/datosMaestrosService';
import useCustomModal from '../../hooks/useCustomModal';
import CustomModal from '../CustomModal';
import MenuAccionesFila from './MenuAccionesFila';
import PanelFiltros, { CampoFiltro } from '../PanelFiltros';
import '../../style/components/GestionDatosMaestros.css';
import { coincideBusqueda } from '../../utils/textoComparable';

// Los servicios se guardan en meses en la BD; en la UI se editan/muestran en años.
const mesesAAnios = (meses) => {
    const m = Number(meses) || 0;
    return Math.round((m / 12) * 100) / 100; // 2 decimales, ej. 25 o 25.5
};
const aniosAMeses = (anios) => Math.round((Number(anios) || 0) * 12);

const formatearAnios = (anios) => {
    const total = Number(anios) || 0;
    const enteros = Math.floor(total);
    const meses = Math.round((total - enteros) * 12);
    if (meses === 0) return `${enteros} años`;
    return `${enteros} a. ${meses} m.`;
};

const FORM_INICIAL = { nombre: '', edadMinima: '', serviciosMinimosAnios: '', alertaPersonalizada: '', activo: true };

const GestionRegimenesDiferenciales = () => {
    const { modalState, hideModal, showModal } = useCustomModal();
    const [regimenes, setRegimenes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModalForm, setShowModalForm] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selected, setSelected] = useState(null);
    const [formData, setFormData] = useState(FORM_INICIAL);
    const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
    const [filterNombre, setFilterNombre] = useState('');

    useEffect(() => { cargarRegimenes(); }, []);

    const cargarRegimenes = async () => {
        setLoading(true);
        try {
            const response = await DatosMaestrosService.getRegimenesDiferenciales(false);
            setRegimenes(Array.isArray(response.data) ? response.data : []);
        } catch {
            mostrarAlerta('Error al cargar los regímenes diferenciales', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const mostrarAlerta = (message, variant) => {
        setAlert({ show: true, message, variant });
        setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 4000);
    };

    const handleOpenModal = (mode, regimen = null) => {
        setModalMode(mode);
        if (mode === 'edit' && regimen) {
            setSelected(regimen);
            setFormData({
                nombre: regimen.nombre,
                edadMinima: regimen.edadMinima ?? '',
                serviciosMinimosAnios: mesesAAnios(regimen.serviciosMinimosMeses),
                alertaPersonalizada: regimen.alertaPersonalizada || '',
                activo: !!regimen.activo,
            });
        } else {
            setSelected(null);
            setFormData(FORM_INICIAL);
        }
        setShowModalForm(true);
    };

    const handleCloseModal = () => {
        setShowModalForm(false);
        setSelected(null);
        setFormData(FORM_INICIAL);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const edad = Number(formData.edadMinima);
        const serviciosMeses = aniosAMeses(formData.serviciosMinimosAnios);
        if (!formData.nombre.trim()) { mostrarAlerta('El nombre es obligatorio', 'warning'); return; }
        if (!(edad > 0 && edad <= 100)) { mostrarAlerta('La edad mínima debe ser válida (en años)', 'warning'); return; }
        if (!(serviciosMeses > 0)) { mostrarAlerta('Los servicios mínimos deben ser válidos (en años)', 'warning'); return; }

        const payload = {
            nombre: formData.nombre.trim(),
            edadMinima: edad,
            serviciosMinimosMeses: serviciosMeses,
            alertaPersonalizada: formData.alertaPersonalizada.trim() || null,
            activo: formData.activo,
        };

        setLoading(true);
        try {
            if (modalMode === 'create') {
                await DatosMaestrosService.createRegimenDiferencial(payload);
                mostrarAlerta('Régimen creado exitosamente', 'success');
            } else {
                await DatosMaestrosService.updateRegimenDiferencial(selected.id, payload);
                mostrarAlerta('Régimen actualizado exitosamente', 'success');
            }
            handleCloseModal();
            cargarRegimenes();
        } catch {
            mostrarAlerta('Error al guardar el régimen', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (regimen) => {
        showModal({
            title: 'Eliminar régimen',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: `¿Seguro que querés eliminar "${regimen.nombre}" del catálogo de regímenes diferenciales?`,
            onConfirm: async () => {
                try {
                    await DatosMaestrosService.deleteRegimenDiferencial(regimen.id);
                    mostrarAlerta('Régimen eliminado exitosamente', 'success');
                    cargarRegimenes();
                } catch {
                    mostrarAlerta('Error al eliminar el régimen', 'danger');
                }
            },
        });
    };

    const regimenesFiltrados = regimenes.filter(r => coincideBusqueda(r.nombre, filterNombre));

    return (
        <div className="gestion-datos-maestros">
            {alert.show && (
                <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: '', variant: '' })}>
                    {alert.message}
                </Alert>
            )}

            <div className="d-flex justify-content-end flex-wrap gap-2 mb-3">
                <Button variant="primary" onClick={() => handleOpenModal('create')}>
                    <i className="bi bi-plus-circle me-2"></i>
                    Nuevo Régimen
                </Button>
            </div>

            <PanelFiltros
                hayFiltros={filterNombre.trim() !== ''}
                onLimpiar={() => setFilterNombre('')}
                resumen={`Mostrando ${regimenesFiltrados.length} de ${regimenes.length} regímenes`}
            >
                <CampoFiltro etiqueta="Buscar" icono="bi-search" htmlFor="filtro-regimen-nombre" ancho>
                    <Form.Control
                        id="filtro-regimen-nombre"
                        className="panel-filtros-input"
                        type="text"
                        placeholder="Nombre del régimen…"
                        value={filterNombre}
                        onChange={(e) => setFilterNombre(e.target.value)}
                    />
                </CampoFiltro>
            </PanelFiltros>

            {loading && regimenes.length === 0 ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div className="table-responsive">
                    <Table hover className="datos-maestros-table">
                        <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Edad mínima</th>
                            <th>Servicios mínimos</th>
                            <th>Alerta</th>
                            <th>Activo</th>
                            <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                        </tr>
                        </thead>
                        <tbody>
                        {regimenesFiltrados.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="text-center text-muted py-4">
                                    {regimenes.length === 0
                                        ? 'No hay regímenes registrados'
                                        : 'No se encontraron regímenes que coincidan con la búsqueda'}
                                </td>
                            </tr>
                        ) : (
                            regimenesFiltrados.map((regimen) => (
                                <tr key={regimen.id}>
                                    <td className="fw-semibold">{regimen.nombre}</td>
                                    <td>{regimen.edadMinima} años</td>
                                    <td>{formatearAnios(mesesAAnios(regimen.serviciosMinimosMeses))}</td>
                                    <td>
                                        {regimen.alertaPersonalizada
                                            ? <i className="bi bi-bell-fill text-warning" title={regimen.alertaPersonalizada}></i>
                                            : <span className="text-muted">—</span>}
                                    </td>
                                    <td>
                                        <span className={`badge ${regimen.activo ? 'bg-success' : 'bg-secondary'}`}>
                                            {regimen.activo ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="text-end">
                                        <MenuAccionesFila acciones={[
                                            { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => handleOpenModal('edit', regimen) },
                                            { etiqueta: 'Eliminar', icono: 'bi-trash', peligro: true, onClick: () => handleDelete(regimen) },
                                        ]} />
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={showModalForm} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Nuevo Régimen Diferencial' : 'Editar Régimen Diferencial'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.nombre}
                                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                required
                                placeholder="Ej: Construcción"
                            />
                        </Form.Group>

                        <Row>
                            <Col xs={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Edad mínima (años) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        max="100"
                                        value={formData.edadMinima}
                                        onChange={(e) => setFormData({ ...formData, edadMinima: e.target.value })}
                                        required
                                        placeholder="Ej: 55"
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Servicios mínimos (años) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        step="0.5"
                                        value={formData.serviciosMinimosAnios}
                                        onChange={(e) => setFormData({ ...formData, serviciosMinimosAnios: e.target.value })}
                                        required
                                        placeholder="Ej: 25"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Alerta personalizada (opcional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={formData.alertaPersonalizada}
                                onChange={(e) => setFormData({ ...formData, alertaPersonalizada: e.target.value })}
                                placeholder="Mensaje a mostrar en la calculadora cuando haya aportes de este régimen"
                                maxLength={500}
                            />
                            <Form.Text className="text-muted">
                                Se muestra en la evaluación de la calculadora cuando el trámite tenga aportes de este régimen.
                            </Form.Text>
                        </Form.Group>

                        <Form.Check
                            type="checkbox"
                            id="regimen-activo"
                            label="Activo"
                            checked={formData.activo}
                            onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <CustomModal {...modalState} onHide={hideModal} />
        </div>
    );
};

export default GestionRegimenesDiferenciales;
